<template>
    <div class="ds-dossier-body">
        <div class="ds-dossier-subhead">
            <button type="button" class="ds-dossier-back" :disabled="pending" @click="emit('back')"><BackIcon />Личные данные</button>
        </div>
        <span class="ds-dossier-subtitle">Новое трудоустройство</span>
        <span class="ds-dossier-muted">{{ fullName(person) || DASH }} · сейчас {{ pluralEmployments(activeEmployments.length) }}, {{ activeShareText }}</span>

        <div class="ds-dossier-grid">
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">Подразделение</span>
                <dropdown v-model="draft.department" :options="departmentStore.myDepartments" :disabled="pending" search>
                    <span class="truncate" :class="{ 'ds-dossier-placeholder': !draft.department }">{{ draft.department?.name || 'Выберите' }}</span>
                </dropdown>
                <span v-if="showError('department')" class="ds-dossier-field-error">{{ errors.department }}</span>
            </div>
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">Должность</span>
                <dropdown v-model="draft.position" :options="scheduleStore.positions" :disabled="pending" search>
                    <span class="truncate" :class="{ 'ds-dossier-placeholder': !draft.position }">{{ draft.position?.name || 'Выберите' }}</span>
                </dropdown>
                <span v-if="showError('position')" class="ds-dossier-field-error">{{ errors.position }}</span>
            </div>
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">Дата начала</span>
                <div class="ds-glass-date"><AppDateInput v-model="hiredDateModel" placeholder="дд.мм.гггг" /></div>
                <span v-if="showError('hiredDate')" class="ds-dossier-field-error">{{ errors.hiredDate }}</span>
            </div>
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">Доля рабочих часов, %</span>
                <base-input :model-value="draft.percentage" :label="false" max-length="3" :disabled="pending" @update:model-value="draft.percentage = String($event).replace(/\D/g, '')" />
                <span v-if="showError('percentage')" class="ds-dossier-field-error">{{ errors.percentage }}</span>
            </div>
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">Доступность</span>
                <dropdown v-model="draft.shiftSchedule" :options="scheduleStore.shiftSchedules" :disabled="pending">
                    <span class="truncate" :class="{ 'ds-dossier-placeholder': !draft.shiftSchedule }">{{ draft.shiftSchedule?.name || 'Выберите' }}</span>
                </dropdown>
                <span v-if="showError('shiftSchedule')" class="ds-dossier-field-error">{{ errors.shiftSchedule }}</span>
            </div>
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">Ставка в час</span>
                <base-input :model-value="draft.salaryHour" :label="false" max-length="7" :disabled="pending" @update:model-value="draft.salaryHour = String($event).replace(/\D/g, '')" />
                <span v-if="showError('salaryHour')" class="ds-dossier-field-error">{{ errors.salaryHour }}</span>
            </div>
        </div>
        <div class="ds-dossier-grid">
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">Табельный код</span>
                <base-input v-model="draft.tabelCode" :label="false" max-length="20" placeholder="—" :disabled="pending" />
            </div>
        </div>

        <!-- Предупреждение не блокирует добавление. -->
        <div v-if="exceedsFullShare" class="ds-dossier-info is-day-state">Сумма долей станет больше 100 % — проверьте, что это верно.</div>
    </div>

    <div class="ds-dossier-footer">
        <span v-if="message" class="ds-dossier-footer-message" :class="{ 'ds-dossier-field-error': !created }" role="alert">
            {{ message }}
            <button v-if="created" type="button" class="ds-dossier-link" @click="finishRead">Обновить</button>
        </span>
        <button type="button" class="ds-glass-btn ds-glass-btn-secondary" :disabled="pending" @click="emit('back')">Отмена</button>
        <button type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="!canSave" @click="save">{{ pending ? 'Добавление…' : 'Добавить' }}</button>
    </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import type { Dayjs } from 'dayjs'
import AppDateInput from '@/ui/AppDateInput.vue'
import { postEmployment } from '@/api/users'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useScheduleStore } from '@/stores/scheduleStore'
import {
    type DossierEmployment,
    type DossierPerson,
    type NewEmploymentDraft,
    DASH,
    buildCreateEmploymentPayload,
    describeWriteError,
    fullName,
    isActiveEmployment,
    newEmploymentDraft,
    pluralEmployments,
    validateNewEmployment,
} from './dossierModel'
import { BackIcon } from './dossierIcons'

// Новое ТУ (вариант B r2): один POST /api/employments/ по правилам прежнего EmploymentsCreate;
// is_main не отправляется, новое ТУ основным принудительно не становится. Server/UI can_create_employment.
const props = defineProps<{
    person: DossierPerson
    employments: DossierEmployment[]
    todayKey: string
    afterWrite: () => Promise<boolean>
}>()
const emit = defineEmits<{ back: [], done: [] }>()

const departmentStore = useDepartmentStore()
const scheduleStore = useScheduleStore()

const draft = reactive<NewEmploymentDraft>(newEmploymentDraft())
const hiredDateModel = computed({
    get: () => draft.hiredDate,
    set: (value: unknown) => { draft.hiredDate = (value as Dayjs | null) ?? null },
})

// «сейчас N трудоустройств, X %»: активные ТУ, X — сумма долей × 100.
const activeEmployments = computed(() => props.employments.filter(item => isActiveEmployment(item, props.todayKey)))
const activeShare = computed(() => Math.round(activeEmployments.value.reduce((sum, item) => sum + (item.percentage ?? 0), 0) * 100))
const activeShareText = computed(() => `${activeShare.value} %`)
const exceedsFullShare = computed(() => /^\d+$/.test(draft.percentage) && activeShare.value + Number(draft.percentage) > 100)

const touched = ref(false)
watch(draft, () => { touched.value = true }, { deep: true })
const errors = computed(() => validateNewEmployment(draft))
const showError = (field: keyof NewEmploymentDraft) => touched.value && Boolean(errors.value[field])

const pending = ref(false)
const created = ref(false)
const message = ref('')
const canSave = computed(() => !pending.value && !created.value && Object.keys(errors.value).length === 0)

async function save() {
    if (!canSave.value) return
    pending.value = true
    message.value = ''
    try {
        await postEmployment(buildCreateEmploymentPayload(props.person.id, draft), { suppressGlobalErrorToast: true })
    } catch (error) {
        message.value = describeWriteError(error, 'Трудоустройство не добавлено')
        pending.value = false
        return
    }
    created.value = true
    await finishRead()
}

// После 201 — перечитать User; ошибка чтения не означает, что ТУ не создано.
async function finishRead() {
    pending.value = true
    const refreshed = await props.afterWrite()
    pending.value = false
    if (refreshed) emit('done')
    else message.value = 'Трудоустройство добавлено, но обновить данные не удалось.'
}
</script>
